import { useEffect, useState, Fragment, useMemo } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDebounce } from '@/lib/useDebounce'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useGetEmployeesQuery,
  useUpdateEmployeeMutation,
  useSyncEmployeeMasterMutation,
  useGetTeamsQuery,
  useGetEmployeeProjectDetailsQuery,
  useGetEmployeeProjectTagBreakdownQuery,
  type Employee,
} from '@/redux/service/employee'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { CustomPagination } from '@/components/CustomPagination'
import {
  ArchiveIcon,
  DollarSignIcon,
  Edit,
  NotebookText,
  RotateCcwIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Filter,
  Search,
  Bot,
  UserCog,
  Eye,
  Folder,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Coins,
  FilterIcon,
  RefreshCw,
  CalendarClock,
  CalendarPlus,
  Mail,
} from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { EmployeeWorkspaceTabs } from '@/components/EmployeeWorkspaceTabs'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'
import {
  cn,
  dateTimeFormat,
  dateFormat,
  dateFormatDMY,
  dateTimeFormatDMY,
  formatHours,
  formatCurrency,
  toTitleCase,
  toISTDateOnly,
} from '@/lib/utils'
import EmloyeeMode from '@/components/EmloyeeMode'
import { ErrorMessage, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Calendar } from '@/components/ui/calendar'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { showLoadingToast, dismissLoadingToast } from '@/lib/toast'
import EmployeeHistory from '@/components/EmployeeHistory'
import LeaveAllocationDialog from '@/components/LeaveAllocationDialog'
import RequestLeaveDialog from '@/components/RequestLeaveDialog'
import { useGetLeaveBalancesQuery } from '@/redux/service/leaveBalance'
import {
  useGetLeaveReportQuery,
  useEmailLeaveReportMutation,
} from '@/redux/service/leaveRequest'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import Teams from '@/components/teams/Teams'
import SkillsInput from '@/components/SkillsInput'
import SkillsManager from '@/components/SkillsManager'
import { Skeleton } from '@/components/ui/skeleton'
import ReusableDateRangePicker, {
  type DateRange,
} from '@/components/ReusableDateRangePicker'
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay'
interface ProjectBreakdownRowProps {
  employeeId: number
  project: {
    project_id: number
    project_name: string
    estimation_hours: number
    total_hours: number
    total_billable_hours: number
    total_non_billable_hours: number
    billable_cost: number | string
    non_billable_cost: number | string
  }
  isExpanded: boolean
  onToggle: () => void
}

const ProjectBreakdownRow: React.FC<ProjectBreakdownRowProps> = ({
  employeeId,
  project,
  isExpanded,
  onToggle,
}) => {
  const { data: tagData, isLoading: isTagLoading } =
    useGetEmployeeProjectTagBreakdownQuery(
      { employeeId, projectId: project.project_id },
      { skip: !isExpanded }
    )

  return (
    <Fragment>
      <TableRow
        className='hover:bg-muted/10 cursor-pointer transition-colors border-b border-border'
        onClick={onToggle}
      >
        <TableCell
          className='truncate max-w-[180px] font-medium text-foreground'
          title={project.project_name}
        >
          {project.project_name}
        </TableCell>

        <TableCell className='text-right text-muted-foreground'>
          {formatHours(project.estimation_hours)}
        </TableCell>

        <TableCell className='text-right font-medium text-foreground'>
          {formatHours(project.total_hours)}
        </TableCell>

        <TableCell className='text-right font-medium text-foreground'>
          {formatHours(project.total_billable_hours)}
        </TableCell>

        <TableCell className='text-right text-muted-foreground'>
          {formatHours(project.total_non_billable_hours)}
        </TableCell>

        <TableCell className='text-right font-medium text-foreground'>
          {typeof project.billable_cost === 'number'
            ? formatCurrency(project.billable_cost)
            : project.billable_cost}
        </TableCell>

        <TableCell className='text-right text-muted-foreground'>
          {typeof project.non_billable_cost === 'number'
            ? formatCurrency(project.non_billable_cost)
            : project.non_billable_cost}
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow className='bg-muted/5 border-b border-border'>
          <TableCell colSpan={7} className='p-4'>
            <div className='pl-6 pr-6 py-4 bg-muted/20 rounded-lg border border-border/60 space-y-3'>
              <div className='text-xs font-bold text-muted-foreground uppercase tracking-wider'>
                Timesheet Tag Breakdown
              </div>

              {isTagLoading ? (
                <div className='flex items-center gap-2 py-4'>
                  <div className='w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin' />
                  <span className='text-xs text-muted-foreground'>
                    Loading tags...
                  </span>
                </div>
              ) : tagData?.data?.tags && tagData.data.tags.length > 0 ? (
                <div className='overflow-x-auto w-full'>
                  <Table className='min-w-[500px] text-xs'>
                    <TableHeader className='bg-transparent border-b border-border/40'>
                      <TableRow className='hover:bg-transparent border-none'>
                        <TableHead className='h-8 py-1 font-semibold text-muted-foreground'>
                          Tag Name
                        </TableHead>

                        <TableHead className='h-8 py-1 font-semibold text-muted-foreground text-right'>
                          Total Hours
                        </TableHead>

                        <TableHead className='h-8 py-1 font-semibold text-muted-foreground text-right'>
                          Billable Hours
                        </TableHead>

                        <TableHead className='h-8 py-1 font-semibold text-muted-foreground text-right'>
                          Non-Billable Hours
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {tagData.data.tags.map((tag, idx) => (
                        <TableRow
                          key={idx}
                          className='hover:bg-transparent border-none'
                        >
                          <TableCell className='py-1.5 font-medium text-foreground'>
                            <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 text-[10px] font-semibold'>
                              {tag.tag}
                            </span>
                          </TableCell>

                          <TableCell className='py-1.5 text-right text-foreground font-medium'>
                            {formatHours(tag.total_hours)}
                          </TableCell>

                          <TableCell className='py-1.5 text-right text-muted-foreground'>
                            {formatHours(tag.billable_hours)}
                          </TableCell>

                          <TableCell className='py-1.5 text-right text-muted-foreground'>
                            {formatHours(tag.non_billable_hours)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className='text-xs text-muted-foreground py-4'>
                  No tag breakdown available for this project.
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}

interface EmployeeFormValues {
  user_id: number
  user_name: string
  user_email: string
  date_joined: string
  archived: boolean
  team_id: number | null
  skills: string[]
  bot: boolean
  profile_picture?: string | null
}

const EmployeeSchema = Yup.object().shape({
  user_name: Yup.string().required('Name is required'),
  user_email: Yup.string().email('Invalid email').required('Email is required'),
  date_joined: Yup.string().required('Joined date is required'),
})

const tabToSalarySetup: Record<number, number> = {
  1: 1, // Active Setup
  2: 2, // Pending Setup
  3: 3, // Archived
}

const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'team', label: 'Team' },
  { key: 'projects', label: 'Projects' },
  { key: 'skills', label: 'Skills' },
  { key: 'hourly_cost', label: 'Hourly Cost' },
  { key: 'date_joined', label: 'Joined Date' },
  { key: 'bot_alert', label: 'Bot Alert' },
]

export default function Employees() {
  const { pathname } = useLocation()
  const isPendingPage = pathname === '/employees/pending'
  const isTeamsPage = pathname === '/employees/teams'
  const isSkillsPage = pathname === '/employees/skills'
  const isEmployeeListPage = !isTeamsPage && !isSkillsPage

  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditTeamSelectOpen, setIsEditTeamSelectOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDialogOpen2, setIsDialogOpen2] = useState(false)
  const [history, setHistory] = useState(false)
  const [emId, setEmId] = useState<number | null>(null)
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [viewDialogTab, setViewDialogTab] = useState<
    'personal' | 'employee' | 'project'
  >('personal')
  const [leaveRequestFilter, setLeaveRequestFilter] = useState<
    'all' | 'Paid Leave' | 'Unpaid Leave' | 'Special Leave' | 'rejected'
  >('all')
  const [expandedProject, setExpandedProject] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [salarySetup, setSalarySetup] = useState<number>(1)
  const [listView, setListView] = useState<'active' | 'archived'>('active')
  const [filterCard, setFilterCard] = useState(false)
  const [botAlertFilter, setBotAlertFilter] = useState('all')
  const [teamFilter, setTeamFilter] = useState('all')
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)
  const [employeeToArchive, setEmployeeToArchive] = useState<Employee | null>(
    null
  )
  const debouncedSearch = useDebounce(searchQuery, 500)
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()
  const showProjectsColumn = !isPendingPage && listView === 'active'
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })

  const [projectSearch, setProjectSearch] = useState('')
  const debouncedProjectSearch = useDebounce(projectSearch, 500)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('employee-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          return parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedVisibility = localStorage.getItem('employee-table-columns')
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility)
      } catch {
        // Fallback
      }
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'employee-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem(
      'employee-table-columns',
      JSON.stringify(selectedColumns)
    )
  }, [selectedColumns])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) => selectedColumns.includes(key)

  const SORT_KEY_MAP: Record<string, string> = {
    name: 'user_name',
    email: 'user_email',
    team: 'team',
    hourly_cost: 'hourly_cost',
    date_joined: 'date_joined',
    bot_alert: 'bot_alerted',
  }

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)

        setOrder(undefined)
      }
    } else {
      setSortBy(column)

      setOrder('ASC')
    }

    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />

    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />

    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setProjectSearch('')
    setDateRange({ from: undefined, to: undefined })
    setBotAlertFilter('all')
    setTeamFilter('all')
    setSortBy(undefined)
    setOrder(undefined)
    setPage(1)
  }

  const { data, isLoading, isFetching, isError } = useGetEmployeesQuery(
    {
      page,
      limit,
      search: debouncedSearch,
      salarySetup,
      ...(botAlertFilter !== 'all' && {
        alertbot: botAlertFilter === 'alerted' ? 1 : 0,
      }),
      ...(teamFilter !== 'all' && { team_id: Number(teamFilter) }),
      sort_by: sortBy,
      order,
    },

    {
      refetchOnMountOrArgChange: true,
      skip: isTeamsPage || isSkillsPage,
    }
  )

  useEffect(() => {
    if (isFetching && !isLoading) {
      showLoadingToast('Loading employees...', 'employees-fetching-toast')
    } else {
      dismissLoadingToast('employees-fetching-toast')
    }
    return () => {
      dismissLoadingToast('employees-fetching-toast')
    }
  }, [isFetching, isLoading])

  const { data: teamsData, isLoading: isTeamsLoading } = useGetTeamsQuery()
  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isFetching: isDetailsFetching,
  } = useGetEmployeeProjectDetailsQuery(
    {
      employeeId: viewingEmployee?.user_id as number,
      search: debouncedProjectSearch,
      startDate: dateRange.from
        ? format(dateRange.from, 'yyyy-MM-dd')
        : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    },
    { skip: !isViewDialogOpen || !viewingEmployee }
  )

  // Leave info shown on the View Details dialog — same balance/consumed
  // shape as the Leave Allocation dialog, and the last 10 requests.
  const { data: viewLeaveBalancesData } = useGetLeaveBalancesQuery(
    { employee_id: viewingEmployee?.user_id ?? 0, limit: 50 },
    { skip: !isViewDialogOpen || !viewingEmployee }
  )
  const { data: viewLeaveReportData } = useGetLeaveReportQuery(
    {
      employee_id: viewingEmployee?.user_id ?? 0,
      limit: 10,
      ...(leaveRequestFilter === 'rejected'
        ? { status: 'rejected' }
        : leaveRequestFilter !== 'all' && { leave_type: leaveRequestFilter }),
    },
    { skip: !isViewDialogOpen || !viewingEmployee }
  )
  const [emailLeaveReport, { isLoading: isEmailingLeaveReport }] =
    useEmailLeaveReportMutation()

  const { hasPageAccess } = useCheckPermission()
  const visibleColumnsCount = useMemo(() => {
    const visibleCount = columns.filter(col => {
      if (!selectedColumns.includes(col.key)) return false
      if (col.key === 'projects' && !showProjectsColumn) return false
      if (col.key === 'hourly_cost' && !hasPageAccess('finance')) return false
      return true
    }).length
    return visibleCount + 1 // +1 for Actions
  }, [columns, selectedColumns, showProjectsColumn, hasPageAccess])

  const [updateEmployee] = useUpdateEmployeeMutation()
  const [syncEmployeeMaster, { isLoading: isSyncingMaster }] =
    useSyncEmployeeMasterMutation()
  const [syncingEmployeeId, setSyncingEmployeeId] = useState<number | null>(
    null
  )
  const [leaveAllocationEmployee, setLeaveAllocationEmployee] =
    useState<Employee | null>(null)
  const [leaveRequestEmployee, setLeaveRequestEmployee] =
    useState<Employee | null>(null)
  const totalPages = data?.pagination?.totalPages ?? 0
  const totalRecords = data?.pagination?.totalRecords ?? 0
  const rows = useMemo<Employee[]>(
    () => (isError ? [] : (data?.data ?? [])),
    [isError, data]
  )
  const handleEdit = (employee: Employee) => {
    setEditingEmployee({ ...employee })
    setIsDialogOpen(true)
  }

  const handleTimesheetGet = (id: number | undefined) => {
    if (!id) return
    setEmId(id)
    setIsDialogOpen2(true)
  }

  const handleHisory = (id: number) => {
    if (!id) return
    setEmId(id)
    setHistory(true)
  }

  const onSubmitEmployeeForm = async (
    values: EmployeeFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      await updateEmployee({
        id: values.user_id,

        data: values,
      }).unwrap()
      setIsDialogOpen(false)
      toast.success('Employee updated successfully')
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  const handleArchiveConfirm = async () => {
    if (!employeeToArchive) return
    try {
      await updateEmployee({
        id: employeeToArchive.user_id,
        data: {
          user_id: employeeToArchive.user_id,
          user_name: employeeToArchive.user_name,
          user_email: employeeToArchive.user_email,
          date_joined: toISTDateOnly(employeeToArchive.date_joined) || '',
          archived: !employeeToArchive.archived,
        },
      }).unwrap()
      toast.success(
        employeeToArchive.archived
          ? 'Employee unarchived successfully'
          : 'Employee archived successfully'
      )
      setIsArchiveConfirmOpen(false)
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Failed to update archive status')
    }
  }

  const handleSendLeaveReport = async () => {
    if (!viewingEmployee) return
    try {
      const res = await emailLeaveReport({
        employee_id: viewingEmployee.user_id,
      }).unwrap()
      toast.success(res.message || 'Leave report emailed to employee')
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Failed to email leave report')
    }
  }

  const handleSyncMaster = async (employee: Employee) => {
    setSyncingEmployeeId(employee.user_id)
    try {
      const res = await syncEmployeeMaster({ id: employee.user_id }).unwrap()
      toast.success(
        res.message || 'Employee synced with ClickUp Employee Master'
      )
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Failed to sync employee with ClickUp')
    } finally {
      setSyncingEmployeeId(null)
    }
  }

  useEffect(() => {
    setPage(1)
  }, [pathname])

  useEffect(() => {
    if (isTeamsPage || isSkillsPage) return
    if (isPendingPage) {
      setSalarySetup(tabToSalarySetup[2] ?? 2)
    } else if (listView === 'archived') {
      setSalarySetup(tabToSalarySetup[3] ?? 3)
    } else {
      setSalarySetup(tabToSalarySetup[1] ?? 1)
    }
  }, [isPendingPage, listView, isTeamsPage, isSkillsPage])

  let tableContent

  if (isLoading || isFetching) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='py-20' />
      </TableRow>
    )
  } else if (rows.length === 0 || isError) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='text-center py-20'>
          <p className='text-muted-foreground text-sm font-medium'>
            No employees found
          </p>
        </TableCell>
      </TableRow>
    )
  } else {
    tableContent = rows.map((employee: Employee, index: number) => (
      <motion.tr
        key={employee.user_id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.03 }}
        className='group hover:bg-muted/30 transition-colors border-b border-border'
      >
        {columns.map(column => {
          if (!isColumnVisible(column.key)) return null
          if (column.key === 'projects' && !showProjectsColumn) return null
          if (column.key === 'hourly_cost' && !hasPageAccess('finance'))
            return null

          switch (column.key) {
            case 'name':
              return (
                <TableCell key={column.key} className='font-medium truncate'>
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border border-border shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200'>
                      {employee.profile_picture ? (
                        <img
                          src={employee?.profile_picture}
                          alt={employee?.user_name}
                          className='w-full h-full object-cover'
                          onError={e => {
                            ;(e.target as HTMLImageElement).style.display =
                              'none'
                          }}
                        />
                      ) : (
                        <span className='text-xs font-bold text-primary'>
                          {employee?.user_name?.charAt(0).toUpperCase() ?? '—'}
                        </span>
                      )}
                    </div>
                    {toTitleCase(employee?.user_name) ?? '—'}
                  </div>
                </TableCell>
              )
            case 'email':
              return (
                <TableCell key={column.key} className='truncate'>
                  {employee?.user_email ?? '—'}
                </TableCell>
              )
            case 'team':
              return (
                <TableCell key={column.key} className='truncate'>
                  {employee.team || '—'}
                </TableCell>
              )
            case 'projects':
              return (
                <TableCell key={column.key} className='truncate max-w-[200px]'>
                  <div className='flex items-center gap-1 flex-nowrap overflow-hidden'>
                    {employee.projects && employee.projects.length > 0 ? (
                      <>
                        {employee.projects.slice(0, 1).map(project => (
                          <span
                            key={project?.list_id}
                            className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium truncate bg-primary/10 text-primary border border-primary/20 max-w-[120px]'
                            title={project?.project_name}
                          >
                            {project?.project_name}
                          </span>
                        ))}

                        {employee?.projects.length > 1 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium  border border-border text-muted-foreground whitespace-nowrap cursor-help'>
                                +{employee?.projects.length - 1}
                              </span>
                            </TooltipTrigger>

                            <TooltipContent className='max-w-[300px]  border border-border bg-background p-3 shadow-2xl rounded-xl'>
                              <div className='space-y-2'>
                                <p className='text-[11px] font-semibold text-muted-foreground mb-2'>
                                  Additional Projects
                                </p>

                                <div className='flex flex-wrap gap-1.5'>
                                  {employee.projects.slice(1).map(p => (
                                    <span
                                      key={p.list_id}
                                      className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary border border-primary/20'
                                    >
                                      {p.project_name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <span className='text-muted-foreground'>—</span>
                    )}
                  </div>
                </TableCell>
              )
            case 'skills':
              return (
                <TableCell key={column.key} className='truncate max-w-[220px]'>
                  {employee.skills && employee.skills.length > 0 ? (
                    <div className='flex items-center gap-1 flex-nowrap overflow-hidden'>
                      {employee.skills.slice(0, 2).map(s => (
                        <span
                          key={s}
                          className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium  border border-border  truncate max-w-[80px]'
                          title={s}
                        >
                          {s}
                        </span>
                      ))}

                      {employee.skills.length > 2 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium  border border-border text-muted-foreground whitespace-nowrap cursor-help'>
                              +{employee.skills.length - 2}
                            </span>
                          </TooltipTrigger>

                          <TooltipContent className='max-w-[300px]  border border-border p-3 shadow-2xl rounded-xl bg-background'>
                            <div className='space-y-2'>
                              <p className='text-[11px] font-semibold text-muted-foreground mb-2'>
                                Additional Skills
                              </p>

                              <div className='flex flex-wrap gap-1.5'>
                                {employee.skills.slice(2).map(s => (
                                  <span
                                    key={s}
                                    className='inline-flex items-center px-2 py-0.5 text-foreground  rounded-full text-[10px] font-medium  border border-border '
                                  >
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <span className='text-muted-foreground'>—</span>
                  )}
                </TableCell>
              )
            case 'hourly_cost':
              return (
                <TableCell key={column.key} className='truncate'>
                  {employee?.hourly_cost ? '$' + employee?.hourly_cost : '—'}
                </TableCell>
              )
            case 'date_joined':
              return (
                <TableCell key={column.key} className='truncate text-center'>
                  {dateTimeFormat(employee.date_joined)}
                </TableCell>
              )
            case 'bot_alert':
              return (
                <TableCell key={column.key} className='truncate text-center'>
                  <div
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200
    ${
      employee.bot_alerted
        ? 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
        : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
    }`}
                  >
                    <span className='w-2 h-2 rounded-full bg-current'></span>
                    {employee.bot_alerted ? 'Alerted' : 'No Alert'}
                  </div>
                </TableCell>
              )
            default:
              return null
          }
        })}
        <TableCell className='truncate sticky right-0 z-10 bg-background/95 backdrop-blur-sm flex items-center gap-2  shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => {
                  setViewingEmployee(employee)
                  setViewDialogTab('personal')
                  setLeaveRequestFilter('all')
                  setIsViewDialogOpen(true)
                }}
              >
                <Eye className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View Details</TooltipContent>
          </Tooltip>
          {!employee.master_synced && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                  disabled={
                    isSyncingMaster && syncingEmployeeId === employee.user_id
                  }
                  onClick={() => handleSyncMaster(employee)}
                >
                  <RefreshCw
                    className={cn(
                      'w-4 h-4',
                      isSyncingMaster &&
                        syncingEmployeeId === employee.user_id &&
                        'animate-spin'
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sync with ClickUp Employee Master</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => handleEdit(employee)}
              >
                <Edit className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit Employee</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => handleHisory(employee.user_id)}
              >
                <DollarSignIcon className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Salary History</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => handleTimesheetGet(employee.clickup_id)}
              >
                <NotebookText className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View Timesheet</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => setLeaveAllocationEmployee(employee)}
              >
                <CalendarClock className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Leave Allocation</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => setLeaveRequestEmployee(employee)}
              >
                <CalendarPlus className='w-4 h-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Request Leave</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border  hover:bg-accent hover:text-accent-foreground hover:border-border'
                onClick={() => {
                  setEmployeeToArchive(employee)

                  setIsArchiveConfirmOpen(true)
                }}
              >
                {employee.archived ? (
                  <RotateCcwIcon className='w-4 h-4' />
                ) : (
                  <ArchiveIcon className='w-4 h-4' />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {employee.archived ? 'Restore Employee' : 'Archive Employee'}
            </TooltipContent>
          </Tooltip>
        </TableCell>
      </motion.tr>
    ))
  }

  return (
    <TooltipProvider>
      <div className='h-full flex flex-col space-y-4 overflow-hidden'>
        <div className='shrink-0 space-y-4'>
          <div className='flex flex-col space-y-6'>
            <div className='flex items-center justify-between' />
          </div>

          <div className='flex flex-col lg:flex-row lg:items-center justify-between border-b border-border pb-3 mb-4 gap-4'>
            <EmployeeWorkspaceTabs />

            {isEmployeeListPage && (
              <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
                {!isPendingPage && (
                  <Select
                    value={listView}
                    onValueChange={(value: 'active' | 'archived') => {
                      setListView(value)
                      setPage(1)
                    }}
                  >
                    <SelectTrigger className='h-9 w-full sm:w-44 shrink-0 border-border bg-card hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='active'>Active Employees</SelectItem>
                      <SelectItem value='archived'>Archived</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className='relative w-full sm:w-48 shrink-0'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                  <Input
                    placeholder='Search by name or email...'
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value)
                      setPage(1)
                    }}
                    className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
                  />
                </div>
                <Button
                  onClick={() => setFilterCard(prev => !prev)}
                  variant='secondary'
                  size='sm'
                  className='flex gap-2 h-9 text-xs border-border w-full sm:w-auto justify-center'
                >
                  <FilterIcon className='w-3 h-3' />
                </Button>
                <ColumnVisibilityPopover
                  columns={columns}
                  isColumnVisible={isColumnVisible}
                  onToggle={toggleColumn}
                  onReset={() => {
                    setColumns(DEFAULT_COLUMNS)
                    setSelectedColumns(DEFAULT_COLUMNS.map(c => c.key))
                  }}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                />
              </div>
            )}
          </div>

          <AnimatePresence>
            {isEmployeeListPage && filterCard && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className='overflow-hidden mb-4'
              >
                <div className='border border-border rounded-xl bg-card p-6 shadow-sm space-y-4'>
                  <div className='flex items-center justify-between px-1'>
                    <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70'>
                      Advanced Filters
                    </div>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={handleClearFilters}
                      className='h-7 px-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                    >
                      <X className='w-3 h-3' /> Reset All
                    </Button>
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                    <Select
                      value={teamFilter}
                      onValueChange={value => {
                        setTeamFilter(value)
                        setPage(1)
                      }}
                      disabled={isTeamsLoading}
                    >
                      <SelectTrigger className='h-9 w-full border-border hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <UserCog className='w-3.5 h-3.5 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue placeholder='Team Filter' />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className='max-h-[300px]'>
                        <SelectItem value='all'>All Teams</SelectItem>
                        {teamsData?.data?.map(t => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={botAlertFilter}
                      onValueChange={value => {
                        setBotAlertFilter(value)
                        setPage(1)
                      }}
                    >
                      <SelectTrigger className='h-9 w-full border-border hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <Bot className='w-3.5 h-3.5 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue placeholder='Bot Alert' />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All Alerts</SelectItem>
                        <SelectItem value='alerted'>Alerted</SelectItem>
                        <SelectItem value='no-alert'>No Alert</SelectItem>
                      </SelectContent>
                    </Select>
                    <ReusableDateRangePicker
                      onSelect={val => {
                        setDateRange(val)
                        setPage(1)
                      }}
                      className='w-full'
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {isTeamsPage ? (
          <div className='flex-1 overflow-auto'>
            <Teams />
          </div>
        ) : isSkillsPage ? (
          <div className='flex-1 overflow-auto'>
            <SkillsManager />
          </div>
        ) : (
          <>
            <div className='flex-1 overflow-hidden border border-border rounded-sm'>
              <Table className='[&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 [&_td]:border-b border-none '>
                <TableHeader>
                  <TableRow className='border-b border-border'>
                    {columns.map(column => {
                      if (!isColumnVisible(column.key)) return null
                      if (column.key === 'projects' && !showProjectsColumn)
                        return null
                      if (
                        column.key === 'hourly_cost' &&
                        !hasPageAccess('finance')
                      )
                        return null

                      const sortKey = SORT_KEY_MAP[column.key]

                      return (
                        <TableHead
                          key={column.key}
                          className={cn(
                            'text-foreground truncate font-medium',
                            sortKey && 'cursor-pointer select-none'
                          )}
                          onClick={
                            sortKey ? () => handleSort(sortKey) : undefined
                          }
                        >
                          <div
                            className={cn(
                              'flex items-center',
                              ['date_joined', 'bot_alert'].includes(
                                column.key
                              ) && 'justify-center'
                            )}
                          >
                            {column.label} {sortKey && getSortIcon(sortKey)}
                          </div>
                        </TableHead>
                      )
                    })}
                    <TableHead className='text-foreground truncate font-medium'>
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>{tableContent}</TableBody>
              </Table>
              {(isLoading || isFetching) && (
                <TableLoadingOverlay label='Loading employees...' />
              )}
            </div>

            {(rows?.length ?? 0) > 0 && !isLoading && (
              <div className='shrink-0 pt-6'>
                <CustomPagination
                  limit={limit}
                  setLimit={setLimit}
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  title='Employees'
                />
              </div>
            )}
          </>
        )}

        <Dialog
          open={isDialogOpen}
          onOpenChange={open => {
            setIsDialogOpen(open)
            if (!open) {
              setIsEditTeamSelectOpen(false)
            }
          }}
        >
          <DialogContent
            className='max-w-2xl h-[650px] p-0  overflow-x-auto bg-background border-border rounded-xl shadow-2xl'
            onInteractOutside={event => {
              if (isEditTeamSelectOpen) {
                event.preventDefault()
              }
            }}
            onPointerDownOutside={event => {
              if (isEditTeamSelectOpen) {
                event.preventDefault()
              }
            }}
            onFocusOutside={event => {
              if (isEditTeamSelectOpen) {
                event.preventDefault()
              }
            }}
          >
            {/* HEADER */}

            <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
              <div className='flex items-center gap-4'>
                <div className='h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm overflow-hidden border border-border'>
                  {editingEmployee?.profile_picture ? (
                    <img
                      src={editingEmployee.profile_picture}
                      alt={editingEmployee.user_name}
                      className='w-full h-full object-cover'
                    />
                  ) : (
                    <UserCog className='w-6 h-6' />
                  )}
                </div>

                <div className='space-y-1'>
                  <DialogTitle className='text-2xl font-bold text-foreground tracking-tight'>
                    Edit Employee
                  </DialogTitle>

                  <DialogDescription className='text-sm text-muted-foreground'>
                    Update professional details and settings for this employee
                  </DialogDescription>
                </div>
              </div>
            </div>
            {editingEmployee && (
              <Formik<EmployeeFormValues>
                enableReinitialize
                initialValues={{
                  user_id: editingEmployee.user_id ?? 0,
                  user_name: editingEmployee.user_name ?? '',
                  user_email: editingEmployee.user_email ?? '',
                  archived: editingEmployee?.archived || false,
                  date_joined: toISTDateOnly(editingEmployee.date_joined) ?? '',
                  team_id: editingEmployee.team_id ?? null,
                  skills: editingEmployee.skills ?? [],
                  bot: editingEmployee.bot_alerted || false,
                  profile_picture: editingEmployee.profile_picture || null,
                }}
                validationSchema={EmployeeSchema}
                onSubmit={onSubmitEmployeeForm}
              >
                {({ values, handleChange, isSubmitting, setFieldValue }) => (
                  <Form className='flex flex-col h-full'>
                    <div className='p-8 space-y-6'>
                      {/* BASIC INFO GRID */}
                      <div className='grid grid-cols-2 gap-6'>
                        {/* Name */}
                        <div className='space-y-2'>
                          <Label
                            htmlFor='user_name'
                            className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
                          >
                            Full Name <span className='text-red-500'>*</span>
                          </Label>
                          <Input
                            id='user_name'
                            name='user_name'
                            placeholder='e.g. John Doe'
                            value={values.user_name}
                            onChange={handleChange}
                            className='h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all'
                          />
                          <ErrorMessage
                            name='user_name'
                            component='p'
                            className='text-[11px] font-medium text-destructive px-1'
                          />
                        </div>
                        {/* Email */}
                        <div className='space-y-2'>
                          <Label
                            htmlFor='user_email'
                            className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
                          >
                            Email Address{' '}
                            <span className='text-red-500'>*</span>
                          </Label>
                          <Input
                            id='user_email'
                            name='user_email'
                            type='email'
                            placeholder='john@example.com'
                            value={values.user_email}
                            onChange={handleChange}
                            className='h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all'
                          />
                          <ErrorMessage
                            name='user_email'
                            component='p'
                            className='text-[11px] font-medium text-destructive px-1'
                          />
                        </div>
                        {/* Team */}
                        <div className='space-y-2'>
                          <Label
                            htmlFor='team_id'
                            className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'
                          >
                            Assigned Team
                          </Label>
                          <Select
                            name='team_id'
                            value={
                              values.team_id != null
                                ? String(values.team_id)
                                : 'none'
                            }
                            onOpenChange={setIsEditTeamSelectOpen}
                            onValueChange={value =>
                              setFieldValue(
                                'team_id',

                                value === 'none' ? null : Number(value)
                              )
                            }
                            disabled={isTeamsLoading}
                          >
                            <SelectTrigger className='h-10 border-border rounded-lg hover:bg-muted/30 transition-all'>
                              <SelectValue placeholder='Select a team' />
                            </SelectTrigger>
                            <SelectContent className='rounded-xl shadow-xl'>
                              <SelectItem value='none'>No Team</SelectItem>
                              {teamsData?.data?.map(t => (
                                <SelectItem key={t.id} value={String(t.id)}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Joined Date */}
                        <div className='space-y-2'>
                          <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                            Joined Date <span className='text-red-500'>*</span>
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant='outline'
                                className={cn(
                                  'w-full justify-start text-left font-medium h-10 border-border rounded-lg hover:bg-muted/30 transition-all',

                                  !values.date_joined && 'text-muted-foreground'
                                )}
                              >
                                {values.date_joined
                                  ? format(
                                      new Date(values.date_joined),

                                      'MMM dd, yyyy'
                                    )
                                  : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className='w-auto p-0 rounded-xl border-border shadow-xl'
                              align='start'
                            >
                              <Calendar
                                mode='single'
                                captionLayout='dropdown'
                                fromYear={2018}
                                selected={
                                  values.date_joined
                                    ? new Date(values.date_joined)
                                    : undefined
                                }
                                onSelect={date =>
                                  setFieldValue(
                                    'date_joined',

                                    date ? format(date, 'yyyy-MM-dd') : ''
                                  )
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <ErrorMessage
                            name='date_joined'
                            component='p'
                            className='text-[11px] font-medium text-destructive px-1'
                          />
                        </div>
                      </div>
                      {/* SKILLS */}
                      <div className='space-y-2'>
                        <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                          Employee Skills
                        </Label>
                        <div className='p-4 rounded-xl border border-border bg-muted/10'>
                          <SkillsInput
                            value={values.skills}
                            onChange={next => setFieldValue('skills', next)}
                            placeholder='Add skills (e.g. React, Node.js)...'
                          />
                          <p className='text-[10px] text-muted-foreground mt-3 flex items-center gap-1.5'>
                            <Filter className='w-3 h-3' />
                            Press Enter or comma to add. Click × to remove.
                          </p>
                        </div>
                      </div>

                      {/* SETTINGS SECTION */}
                      <div className='pt-2'>
                        <div className='p-4 rounded-xl border border-border bg-muted/20 flex items-center justify-between'>
                          <div className='space-y-0.5'>
                            <Label
                              htmlFor='bot'
                              className='text-sm font-bold text-foreground'
                            >
                              Time Sheet Alerts
                            </Label>
                            <p className='text-xs text-muted-foreground'>
                              Enable automated bot alerts for this account
                            </p>
                          </div>
                          <div className='flex items-center gap-3'>
                            <span
                              className={cn(
                                'text-[11px] font-bold uppercase tracking-wider transition-colors',

                                values.bot
                                  ? 'text-primary'
                                  : 'text-muted-foreground'
                              )}
                            >
                              {values.bot ? 'Active' : 'Disabled'}
                            </span>

                            <Switch
                              id='bot'
                              checked={values.bot}
                              onCheckedChange={checked =>
                                setFieldValue('bot', checked)
                              }
                              className='data-[state=checked]:bg-primary'
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* FOOTER */}
                    <div className='px-8 py-4 border-t border-border flex justify-end gap-3 bg-muted/10'>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => setIsDialogOpen(false)}
                        className='px-6 rounded-lg border-border hover:bg-muted transition-all'
                      >
                        Cancel
                      </Button>

                      <Button
                        type='submit'
                        disabled={isSubmitting}
                        className='px-10 rounded-lg shadow-sm shadow-primary/10'
                      >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className='w-full max-w-6xl max-h-[90vh] p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl flex flex-col'>
            {/* Header */}

            <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/20 shrink-0'>
              <div className='flex items-center gap-4'>
                <div className='h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm overflow-hidden border border-border'>
                  {viewingEmployee?.profile_picture ? (
                    <img
                      src={viewingEmployee.profile_picture}
                      alt={viewingEmployee.user_name}
                      className='w-full h-full object-cover'
                    />
                  ) : (
                    <span className='text-lg font-bold'>
                      {viewingEmployee?.user_name?.charAt(0).toUpperCase() ||
                        'E'}
                    </span>
                  )}
                </div>

                <div className='space-y-0.5'>
                  <DialogTitle className='text-2xl font-bold text-foreground tracking-tight flex items-center gap-3.5'>
                    {toTitleCase(viewingEmployee?.user_name) ||
                      'Employee Details'}

                    {viewingEmployee?.team && (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20'>
                        {viewingEmployee.team}
                      </span>
                    )}
                  </DialogTitle>

                  <DialogDescription className='text-sm text-muted-foreground'>
                    Detailed overview of performance, worked hours, and cost
                    metrics
                  </DialogDescription>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className='px-6 pt-3 shrink-0'>
              <div className='flex p-1 bg-muted border border-border rounded-xl gap-1 w-fit h-9 items-stretch mb-[-1px]'>
                {(
                  [
                    { key: 'personal', label: 'Personal' },
                    { key: 'employee', label: 'Employee' },
                    { key: 'project', label: 'Project' },
                  ] as const
                ).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setViewDialogTab(tab.key)}
                    className={cn(
                      'px-4 text-xs font-medium transition-all duration-200 rounded-lg flex items-center justify-center',
                      viewDialogTab === tab.key
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className='p-6 space-y-6 overflow-y-auto flex-1'>
              {viewDialogTab === 'personal' &&
                (viewingEmployee?.master_synced_at ? (
                  <div className='rounded-xl border border-border bg-muted/10 p-4'>
                    <h4 className='text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3'>
                      Personal Details
                    </h4>
                    <div className='grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-5 text-sm'>
                      {[
                        ['Gender', viewingEmployee.gender],
                        ['Marital Status', viewingEmployee.marital_status],
                        [
                          'Date of Birth',
                          viewingEmployee.date_of_birth
                            ? dateFormat(viewingEmployee.date_of_birth)
                            : viewingEmployee.date_of_birth,
                        ],
                        ['Blood Group', viewingEmployee.blood_group],
                        ['Phone', viewingEmployee.phone],
                        ['Personal Email', viewingEmployee.personal_email],
                        [
                          'Emergency Contact',
                          viewingEmployee.emergency_contact_name,
                        ],
                        [
                          'Emergency Contact #',
                          viewingEmployee.emergency_contact_number,
                        ],
                        ['Current Location', viewingEmployee.current_location],
                      ]
                        .filter(
                          ([, value]) =>
                            value !== null &&
                            value !== undefined &&
                            value !== ''
                        )
                        .map(([label, value]) => (
                          <div key={label as string}>
                            <div className='text-muted-foreground text-xs mb-0.5'>
                              {label}
                            </div>
                            <div className='font-medium text-foreground break-words'>
                              {value}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className='py-20 text-center text-muted-foreground'>
                    Personal details aren't synced from ClickUp yet.
                  </div>
                ))}
              {viewDialogTab === 'employee' && (
                <>
                  {viewingEmployee?.master_synced_at && (
                    <div className='rounded-xl border border-border bg-muted/10 p-4'>
                      <h4 className='text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3'>
                        Employment Details
                      </h4>
                      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-4 gap-x-5 text-sm'>
                        {[
                          ['Employee Code', viewingEmployee.employee_code],
                          ['Job Title', viewingEmployee.job_title],
                          ['Department', viewingEmployee.department],
                          ['WV Unit', viewingEmployee.wv_unit],
                          ['Employment Type', viewingEmployee.employment_type],
                          ['Official Email', viewingEmployee.official_email],
                          [
                            'Joining Date',
                            viewingEmployee.joining_date
                              ? dateFormat(viewingEmployee.joining_date)
                              : viewingEmployee.joining_date,
                          ],
                          [
                            'Confirmation Date',
                            viewingEmployee.confirmation_date
                              ? dateFormat(viewingEmployee.confirmation_date)
                              : viewingEmployee.confirmation_date,
                          ],
                          [
                            'Probation End Date',
                            viewingEmployee.probation_end_date
                              ? dateFormat(viewingEmployee.probation_end_date)
                              : viewingEmployee.probation_end_date,
                          ],
                          [
                            'Notice Period (days)',
                            viewingEmployee.notice_period_days,
                          ],
                          [
                            'Reporting Manager',
                            viewingEmployee.reporting_manager_name,
                          ],
                          ['Status', toTitleCase(viewingEmployee.emp_status)],
                        ]
                          .filter(
                            ([, value]) =>
                              value !== null &&
                              value !== undefined &&
                              value !== ''
                          )
                          .map(([label, value]) => (
                            <div key={label as string}>
                              <div className='text-muted-foreground text-xs mb-0.5'>
                                {label}
                              </div>
                              <div className='font-medium text-foreground break-words'>
                                {value}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Leave — balances + recent requests for this employee (same
                  data the Leave Allocation dialog and Holidays "Leaves" tab
                  use, just read-only and scoped to this one employee). */}
                  <div className='rounded-xl border border-border bg-muted/10 p-4 space-y-4'>
                    <div className='flex items-center justify-between'>
                      <h4 className='text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2'>
                        <CalendarClock className='w-3.5 h-3.5' />
                        Leave
                      </h4>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        disabled={isEmailingLeaveReport}
                        onClick={handleSendLeaveReport}
                        className='h-7 gap-1.5 rounded-lg text-xs'
                      >
                        <Mail className='w-3.5 h-3.5' />
                        {isEmailingLeaveReport
                          ? 'Sending...'
                          : 'Send Report to Employee'}
                      </Button>
                    </div>

                    <div className='space-y-2'>
                      <p className='text-[11px] font-semibold text-muted-foreground'>
                        Balances
                      </p>
                      <div className='rounded-lg border border-border overflow-hidden'>
                        <Table wrapperClassName='h-auto max-h-[240px] overflow-y-auto'>
                          <TableHeader className='static shadow-sm [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-muted/95'>
                            <TableRow>
                              <TableHead className='text-xs'>Type</TableHead>
                              <TableHead className='text-xs text-right'>
                                Assigned
                              </TableHead>
                              <TableHead className='text-xs text-right'>
                                Consumed
                              </TableHead>
                              <TableHead className='text-xs text-right'>
                                Balance
                              </TableHead>
                              <TableHead className='text-xs'>
                                Validity
                              </TableHead>
                              <TableHead className='text-xs'>Status</TableHead>
                              <TableHead className='text-xs'>Created</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(viewLeaveBalancesData?.data?.length ?? 0) ===
                            0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={7}
                                  className='h-16 text-center text-xs text-muted-foreground'
                                >
                                  No leave balances allocated
                                </TableCell>
                              </TableRow>
                            ) : (
                              viewLeaveBalancesData?.data?.map(b => (
                                <TableRow key={b.id}>
                                  <TableCell className='text-sm font-medium'>
                                    {b.leave_type}
                                  </TableCell>
                                  <TableCell className='text-sm text-right'>
                                    {b.total_assigned}
                                  </TableCell>
                                  <TableCell className='text-sm text-right'>
                                    {b.consumed}
                                  </TableCell>
                                  <TableCell
                                    className={cn(
                                      'text-sm text-right font-semibold',
                                      b.balance <= 0 && 'text-destructive'
                                    )}
                                  >
                                    {b.balance}
                                  </TableCell>
                                  <TableCell className='text-xs text-muted-foreground whitespace-nowrap'>
                                    {dateFormatDMY(b.start_date)} –{' '}
                                    {dateFormatDMY(b.end_date)}
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className={cn(
                                        'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase',
                                        b.status === 'active'
                                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          : 'bg-slate-50 text-slate-500 border border-slate-200'
                                      )}
                                    >
                                      {b.status}
                                    </span>
                                  </TableCell>
                                  <TableCell className='text-xs text-muted-foreground whitespace-nowrap'>
                                    {dateTimeFormatDMY(b.created_at)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <div className='flex items-center justify-between flex-wrap gap-2'>
                        <p className='text-[11px] font-semibold text-muted-foreground'>
                          Recent Requests
                        </p>
                        <div className='flex p-1 bg-muted border border-border rounded-xl gap-1 h-8 items-stretch'>
                          {(
                            [
                              { key: 'all', label: 'All' },
                              { key: 'Paid Leave', label: 'Paid' },
                              { key: 'Unpaid Leave', label: 'Unpaid' },
                              { key: 'Special Leave', label: 'Special' },
                              { key: 'rejected', label: 'Rejected' },
                            ] as const
                          ).map(tab => (
                            <button
                              key={tab.key}
                              type='button'
                              onClick={() => setLeaveRequestFilter(tab.key)}
                              className={cn(
                                'min-w-[64px] px-3 text-xs font-medium rounded-lg flex items-center justify-center transition-all duration-200',
                                leaveRequestFilter === tab.key
                                  ? 'bg-primary text-primary-foreground shadow-sm'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                              )}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className='rounded-lg border border-border overflow-hidden'>
                        <Table wrapperClassName='h-auto max-h-[240px] overflow-y-auto'>
                          <TableHeader className='static bg-transparent shadow-sm [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-muted/95'>
                            <TableRow>
                              <TableHead className='text-xs'>Type</TableHead>
                              <TableHead className='text-xs'>
                                Duration
                              </TableHead>
                              <TableHead className='text-xs'>From</TableHead>
                              <TableHead className='text-xs'>To</TableHead>
                              <TableHead className='text-xs text-right'>
                                Days
                              </TableHead>
                              <TableHead className='text-xs text-right'>
                                Reason
                              </TableHead>
                              <TableHead className='text-xs'>Status</TableHead>
                              <TableHead className='text-xs'>
                                Requested
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(viewLeaveReportData?.data?.length ?? 0) === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={8}
                                  className='h-16 text-center text-xs text-muted-foreground'
                                >
                                  No leave requests found
                                </TableCell>
                              </TableRow>
                            ) : (
                              viewLeaveReportData?.data?.map(r => (
                                <TableRow key={r.id}>
                                  <TableCell className='text-sm font-medium'>
                                    {r.leave_type}
                                  </TableCell>
                                  <TableCell className='text-sm text-muted-foreground'>
                                    {r.leave_duration}
                                  </TableCell>
                                  <TableCell className='text-sm text-muted-foreground whitespace-nowrap'>
                                    {dateFormatDMY(r.from_date)}
                                  </TableCell>
                                  <TableCell className='text-sm text-muted-foreground whitespace-nowrap'>
                                    {dateFormatDMY(r.to_date)}
                                  </TableCell>
                                  <TableCell className='text-sm text-right'>
                                    {Number(r.total_leave_days).toFixed(1)}
                                  </TableCell>
                                  <TableCell className='w-[240px]'>
                                    <div
                                      className='max-w-[220px] line-clamp-2 break-words'
                                      title={r.reason ?? undefined}
                                    >
                                      {r.reason ?? '-'}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <span className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-muted text-muted-foreground border border-border'>
                                      {r.status}
                                    </span>
                                  </TableCell>
                                  <TableCell className='text-xs text-muted-foreground whitespace-nowrap'>
                                    {dateTimeFormatDMY(r.created_at)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {viewDialogTab === 'project' && (
                <>
                  {/* Stat Cards Grid */}
                  <div className='flex gap-3 justify-items-end'>
                    <div className='relative flex-1'>
                      <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground' />
                      <Input
                        placeholder='Search projects...'
                        value={projectSearch}
                        onChange={e => setProjectSearch(e.target.value)}
                        className='h-10 pl-9 border-border rounded-xl bg-card font-medium text-xs focus-visible:ring-primary/20'
                      />
                    </div>
                    <ReusableDateRangePicker
                      value={dateRange}
                      onSelect={val => {
                        setDateRange(val)
                      }}
                      className='flex-1'
                    />
                  </div>

                  {(isDetailsLoading || isDetailsFetching) && !detailsData ? (
                    <div className='space-y-6'>
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                        {[1, 2, 3, 4, 5, 6].map(i => (
                          <Skeleton key={i} className='h-24 rounded-xl' />
                        ))}
                      </div>
                      <div className='space-y-4'>
                        <div className='flex items-center justify-between'>
                          <Skeleton className='h-6 w-48' />
                        </div>
                        <div className='border border-border rounded-xl overflow-hidden'>
                          <Table>
                            <TableBody>
                              {[...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                  <TableCell>
                                    <Skeleton className='h-4 w-32' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-16 ml-auto' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-16 ml-auto' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-16 ml-auto' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-16 ml-auto' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-20 ml-auto' />
                                  </TableCell>
                                  <TableCell>
                                    <Skeleton className='h-4 w-20 ml-auto' />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  ) : detailsData?.data ? (
                    <>
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                        {isDetailsFetching ? (
                          [1, 2, 3, 4, 5, 6].map(i => (
                            <Skeleton key={i} className='h-24 rounded-xl' />
                          ))
                        ) : (
                          <>
                            {/* Total Projects */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Total Projects
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      detailsData.data.summary
                                        .total_projects === 0 &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {detailsData.data.summary.total_projects ||
                                      'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <Folder className='w-5 h-5' />
                                </div>
                              </div>
                            </div>

                            {/* Total Hours */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Total Hours
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      formatHours(
                                        detailsData.data.summary.total_hours
                                      ) === '—' &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {formatHours(
                                      detailsData.data.summary.total_hours
                                    ) !== '—'
                                      ? `${formatHours(detailsData.data.summary.total_hours)}h`
                                      : 'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <Clock className='w-5 h-5' />
                                </div>
                              </div>
                            </div>

                            {/* Total Billable Hours */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Total Billable Hours
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      formatHours(
                                        detailsData.data.summary
                                          .total_billable_hours
                                      ) === '—' &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {formatHours(
                                      detailsData.data.summary
                                        .total_billable_hours
                                    ) !== '—'
                                      ? `${formatHours(detailsData.data.summary.total_billable_hours)}h`
                                      : 'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <CheckCircle2 className='w-5 h-5' />
                                </div>
                              </div>
                            </div>

                            {/* Total Non-Billable Hours */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Total Non-Billable Hours
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      formatHours(
                                        detailsData.data.summary
                                          .total_non_billable_hours
                                      ) === '—' &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {formatHours(
                                      detailsData.data.summary
                                        .total_non_billable_hours
                                    ) !== '—'
                                      ? `${formatHours(detailsData.data.summary.total_non_billable_hours)}h`
                                      : 'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <XCircle className='w-5 h-5' />
                                </div>
                              </div>
                            </div>

                            {/* Billable Cost */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Billable Cost
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      (detailsData.data.summary
                                        .billable_cost === 0 ||
                                        detailsData.data.summary
                                          .billable_cost === '0' ||
                                        detailsData.data.summary
                                          .billable_cost === '$  0.00') &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {detailsData.data.summary.billable_cost &&
                                    detailsData.data.summary.billable_cost !==
                                      0 &&
                                    detailsData.data.summary.billable_cost !==
                                      '0' &&
                                    detailsData.data.summary.billable_cost !==
                                      '$  0.00'
                                      ? typeof detailsData.data.summary
                                          .billable_cost === 'number'
                                        ? formatCurrency(
                                            detailsData.data.summary
                                              .billable_cost
                                          )
                                        : detailsData.data.summary.billable_cost
                                      : 'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <TrendingUp className='w-5 h-5' />
                                </div>
                              </div>
                            </div>

                            {/* Non-Billable Cost */}
                            <div className='relative overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm hover:shadow-md transition-all duration-300 group'>
                              <div className='flex justify-between items-start'>
                                <div className='space-y-2'>
                                  <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Non-Billable Cost
                                  </p>
                                  <h4
                                    className={cn(
                                      'text-2xl font-extrabold text-foreground tracking-tight group-hover:scale-[1.02] transition-transform duration-300',
                                      (detailsData.data.summary
                                        .non_billable_cost === 0 ||
                                        detailsData.data.summary
                                          .non_billable_cost === '0' ||
                                        detailsData.data.summary
                                          .non_billable_cost === '$  0.00') &&
                                        'text-lg font-semibold text-muted-foreground'
                                    )}
                                  >
                                    {detailsData.data.summary
                                      .non_billable_cost &&
                                    detailsData.data.summary
                                      .non_billable_cost !== 0 &&
                                    detailsData.data.summary
                                      .non_billable_cost !== '0' &&
                                    detailsData.data.summary
                                      .non_billable_cost !== '$  0.00'
                                      ? typeof detailsData.data.summary
                                          .non_billable_cost === 'number'
                                        ? formatCurrency(
                                            detailsData.data.summary
                                              .non_billable_cost
                                          )
                                        : detailsData.data.summary
                                            .non_billable_cost
                                      : 'No data found'}
                                  </h4>
                                </div>
                                <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                                  <Coins className='w-5 h-5' />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Projects Table */}

                      <div className='space-y-4'>
                        <div className='flex items-center justify-between'>
                          <h3 className='text-lg font-bold text-foreground flex items-center gap-2'>
                            <Folder className='w-4.5 h-4.5 text-primary' />
                            Project Breakdown
                          </h3>
                        </div>

                        <div className='border border-border rounded-xl overflow-hidden shadow-sm bg-card w-full'>
                          <Table wrapperClassName='h-auto max-h-[600px] overflow-y-auto'>
                            <TableHeader className='static rounded-xl bg-transparent shadow-sm [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-muted/95 [&_th]:backdrop-blur-sm'>
                              <TableRow>
                                <TableHead className='font-bold text-foreground truncate'>
                                  Project Name
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate'>
                                  Estimate Hours
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate'>
                                  Total Hours
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate'>
                                  Billable Hours
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate '>
                                  Non-Billable Hours
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate'>
                                  Billable Cost
                                </TableHead>

                                <TableHead className='font-bold text-foreground text-right truncate'>
                                  Non-Billable Cost
                                </TableHead>
                              </TableRow>
                            </TableHeader>

                            <TableBody>
                              {isDetailsFetching ? (
                                [...Array(5)].map((_, i) => (
                                  <TableRow key={i}>
                                    <TableCell>
                                      <Skeleton className='h-4 w-32' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-16 ml-auto' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-16 ml-auto' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-16 ml-auto' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-16 ml-auto' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-20 ml-auto' />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton className='h-4 w-20 ml-auto' />
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : detailsData.data.project_breakdown &&
                                detailsData.data.project_breakdown.length >
                                  0 ? (
                                detailsData.data.project_breakdown.map(
                                  project => (
                                    <ProjectBreakdownRow
                                      key={project.project_id}
                                      employeeId={viewingEmployee?.user_id ?? 0}
                                      project={project}
                                      isExpanded={
                                        expandedProject === project.project_id
                                      }
                                      onToggle={() =>
                                        setExpandedProject(
                                          expandedProject === project.project_id
                                            ? null
                                            : project.project_id
                                        )
                                      }
                                    />
                                  )
                                )
                              ) : (
                                <TableRow>
                                  <TableCell
                                    colSpan={7}
                                    className='h-24 text-center text-muted-foreground'
                                  >
                                    No data found
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className='py-20 text-center text-muted-foreground'>
                      No project data available for this employee.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}

            <div className='px-8 py-4 border-t border-border flex justify-end bg-muted/10 shrink-0'>
              <Button
                type='button'
                onClick={() => {
                  setIsViewDialogOpen(false)

                  setExpandedProject(null)
                }}
                className='px-6 rounded-lg '
              >
                Close View
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <EmloyeeMode
          id={emId ?? undefined}
          isOpen={isDialogOpen2}
          isClose={() => setIsDialogOpen2(false)}
        />

        <EmployeeHistory
          id={emId ?? 0}
          isOpen={history}
          onClose={() => setHistory(false)}
        />

        {leaveAllocationEmployee && (
          <LeaveAllocationDialog
            employeeId={leaveAllocationEmployee.user_id}
            employeeName={leaveAllocationEmployee.user_name}
            isOpen={Boolean(leaveAllocationEmployee)}
            isClose={() => setLeaveAllocationEmployee(null)}
          />
        )}

        {leaveRequestEmployee && (
          <RequestLeaveDialog
            employeeId={leaveRequestEmployee.user_id}
            employeeName={leaveRequestEmployee.user_name}
            isOpen={Boolean(leaveRequestEmployee)}
            isClose={() => setLeaveRequestEmployee(null)}
          />
        )}

        <Dialog
          open={isArchiveConfirmOpen}
          onOpenChange={setIsArchiveConfirmOpen}
        >
          <DialogContent className='max-w-sm  p-6 bg-background border border-border rounded-xl shadow-2xl'>
            <DialogHeader className='space-y-2'>
              <div
                className={cn(
                  'h-12 w-12 rounded-full flex items-center justify-center mb-2',

                  employeeToArchive?.archived
                    ? 'bg-primary/10'
                    : 'bg-destructive/10'
                )}
              >
                {employeeToArchive?.archived ? (
                  <RotateCcwIcon className='h-6 w-6 text-primary' />
                ) : (
                  <ArchiveIcon className='h-6 w-6 text-destructive' />
                )}
              </div>

              <DialogTitle className='text-xl font-bold'>
                {employeeToArchive?.archived
                  ? 'Restore Employee'
                  : 'Archive Employee'}
              </DialogTitle>

              <DialogDescription className='text-sm text-muted-foreground'>
                Are you sure you want to{' '}
                {employeeToArchive?.archived ? 'restore' : 'archive'}{' '}
                <span className='font-bold text-foreground'>
                  {toTitleCase(employeeToArchive?.user_name)}
                </span>
                ?
              </DialogDescription>
            </DialogHeader>

            <div className='flex justify-end gap-3 mt-6'>
              <Button
                variant='outline'
                onClick={() => setIsArchiveConfirmOpen(false)}
                className='flex-1 rounded-lg border-border'
              >
                Cancel
              </Button>

              <Button
                variant={
                  employeeToArchive?.archived ? 'default' : 'destructive'
                }
                onClick={handleArchiveConfirm}
                className='flex-1 rounded-lg'
              >
                {employeeToArchive?.archived
                  ? 'Confirm Restore'
                  : 'Confirm Archive'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}
